import React, { useState, useEffect } from 'react';

// MUI
import { Grid, styled } from '@mui/material';
import { TextField } from '@mui/material';
import { InputAdornment } from '@mui/material';
import { Search } from '@mui/icons-material';

// Util
import NotesFactory from '../../../ProjectCanvas/NotesRegistry';
import { StyledTray, StyledTrayContent } from './StyledTray';
import TrayHeader from './TrayHeader';

const NoteDisplayName = styled('p')(({ theme }) => ({
  margin: '0',
  padding: '0',
  fontFamily: 'Open Sans',
  fontSize: '14px',
  color: theme.palette.text.primary,
  transition: 'color 0.2s ease',
}));

const NoteDescription = styled('p')(({ theme }) => ({
  margin: '4px 0 0 0',
  padding: '0',
  color: theme.palette.text.secondary,
  fontSize: '12px',
  fontFamily: 'Open Sans',
  lineHeight: '1.5em',
}));

// Default markdown content for a new note
const DEFAULT_NOTE =
  'Use sticky notes to document your process, map out steps or write comments for others to see';

function NotesTray({ classes, onClose }) {
  const [notesDisplayData, setNotesDisplayData] = useState(NotesFactory);

  const handleNotesSearch = event => {
    const word = event.target.value.toLowerCase();
    let searchComp = {};
    let usedData;

    const filteredComp = Object.keys(NotesFactory).filter((a, i) => {
      const comp = NotesFactory[a];
      const compName = comp.displayName.toLowerCase().trim();
      const compDescription = comp.description.toLowerCase().trim();
      if (compName.indexOf(word) !== -1) searchComp[a] = NotesFactory[a];
      if (compDescription.indexOf(word) !== -1) searchComp[a] = NotesFactory[a];
      return false;
    });

    if (word !== null && word.trim() !== '') {
      usedData = searchComp;
    } else {
      usedData = NotesFactory;
    }

    setNotesDisplayData(usedData);
  };

  const SidebarNotesItem = component => {
    const onDragStart = (event, component) => {
      let noteData = handleCreatingNote(component.component.name);

      event.dataTransfer.setData('application/reactflow', 'note');
      event.dataTransfer.setData('type', component.component.name);

      // Get note data and send over
      event.dataTransfer.setData('width', noteData.width);
      event.dataTransfer.setData('height', noteData.height);
      event.dataTransfer.setData('color', noteData.color);
      event.dataTransfer.setData('content', noteData.content);

      event.dataTransfer.effectAllowed = 'move';
    };

    return (
      <div
        draggable={true}
        onDragStart={event => onDragStart(event, component)}
        className={classes.canvasItem}
        style={{
          display: 'flex',
          marginBottom: '8px',
        }}
      >
        <div
          style={{
            marginRight: '16px',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <img
            style={{
              height: '41px',
              transition: 'transform 0.2s ease',
            }}
            src={component.component.logo}
          />
        </div>
        <div
          style={{ display: 'flex', flexDirection: 'column', flex: 1, padding: '0', margin: '0' }}
        >
          <div>
            <NoteDisplayName>{component.component.displayName}</NoteDisplayName>
          </div>
          <div>
            <NoteDescription>{component.component.description}</NoteDescription>
          </div>
        </div>
      </div>
    );
  };

  const handleCreatingNote = component_name => {
    switch (component_name) {
      case 'sticky_note':
        return getNoteObject(400, 400, '#FBF6BE', DEFAULT_NOTE);
      case 'small_note':
        return getNoteObject(400, 150, '#FBF6BE', DEFAULT_NOTE);
      case 'large_note':
        return getNoteObject(600, 600, '#FBF6BE', DEFAULT_NOTE);
      case 'full_page':
        return getNoteObject(800, 1200, '#F1F1F1', DEFAULT_NOTE);
      case 'swimlane':
        return getNoteObject(1600, 300, '#DAEEFF', DEFAULT_NOTE);
      default:
        break;
    }
  };

  //create note
  const getNoteObject = (width, height, color, note_content) => {
    return {
      content: note_content,
      color: color,
      width: width,
      height: height,
    };
  };

  return (
    <StyledTray>
      <TrayHeader title="Notes" onClose={onClose} />
      <StyledTrayContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              className={classes.search}
              placeholder="Search for note type"
              id="notes-search"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              onChange={handleNotesSearch}
              variant="outlined"
              size="small"
              sx={{ marginBottom: '4px' }}
            />
          </Grid>
          <Grid item xs={12}>
            {Object.values(notesDisplayData).map((component, id) => {
              return <SidebarNotesItem key={id} component={component} />;
            })}
          </Grid>
        </Grid>
      </StyledTrayContent>
    </StyledTray>
  );
}

export default NotesTray;
